import { useState, useCallback, useRef, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Menu, X, User, ChevronDown, ChevronsLeft, Search, RefreshCcw } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { InstitutionPausedNotice } from "@/components/institutions/InstitutionPausedNotice";
import DashboardHeader from "@/components/DashboardHeader";
import { useNavigate, useLocation } from "react-router-dom";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SocialIcons from "@/components/SocialIcons";
import VideoLibraryMegaMenu from "@/components/navigation/VideoLibraryMegaMenu";
import CartDrawer from "@/components/video-library/CartDrawer";
import HeaderSearch from "@/components/HeaderSearch";
import { useMenu } from "@/hooks/useMenu";
import { useUserInstitution } from "@/hooks/useUserInstitution";
import { useSiteSettings, useApplySiteAccent } from "@/lib/useSiteSettings";
import i18n from "i18next";


type NavigationProps = {
  desktopActions?: ReactNode;
};

const Navigation = ({ desktopActions }: NavigationProps = {}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isLibraryMobileOpen, setIsLibraryMobileOpen] = useState(true);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isMoreMobileOpen, setIsMoreMobileOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const libraryCloseTimer = useRef<number | null>(null);
  const moreCloseTimer = useRef<number | null>(null);
  const { user, userRoles, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('landing');

  // Mobile drawer: push #root left, lock body scroll, close on Escape
  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;
    if (isMenuOpen) {
      root.classList.add('menu-push-open');
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setIsMenuOpen(false);
      };
      window.addEventListener('keydown', onKey);
      return () => {
        root.classList.remove('menu-push-open');
        document.body.style.overflow = prevOverflow;
        window.removeEventListener('keydown', onKey);
      };
    }
  }, [isMenuOpen]);

  
  const isIndexPage = location.pathname === '/';

  // IMPORTANT: call every hook unconditionally BEFORE any early return so hook
  // order stays stable when auth/role state changes (e.g. cross-tab SIGNED_IN).
  const { data: headerTree } = useMenu("header");
  const { data: siteSettings } = useSiteSettings();
  useApplySiteAccent();

  // Institution contacts/members get a switch between the institution stats
  // portal (/staff/institution) and their normal user dashboard.
  const { data: userInstitution } = useUserInstitution(user?.id);
  const isInstitutionPortal = location.pathname.startsWith('/staff/institution');
  const getDashboardRoute = useCallback(() => {
    if (!userRoles.length) return '/dashboard';
    const isInstitutionStaff =
      !!userInstitution &&
      userRoles.includes('staff') &&
      !userRoles.includes('admin') &&
      !userRoles.includes('dev');
    if (isInstitutionStaff) return '/student/dashboard';
    const roleHierarchy: string[] = ['dev', 'admin', 'staff', 'instructor'];
    const hasHighRole = userRoles.some(r => roleHierarchy.includes(r));
    if (hasHighRole) return '/dashboard';
    if (userRoles.includes('student')) return '/student/dashboard';
    return '/guest/dashboard';
  }, [userInstitution, userRoles]);
  const institutionToggleLabel = isInstitutionPortal
    ? t('admin:dashboard.header.user_panel', 'My Dashboard')
    : t('admin:dashboard.header.institution_panel', 'Institution Panel');
  const handleSwitchInstitution = () => {
    if (isInstitutionPortal) {
      navigate('/student/dashboard');
    } else {
      navigate('/staff/institution');
    }
  };

  type MenuChild = {
    label: string;
    href: string;
    page: string;
  };

  type MenuItem = {
    label: string;
    href?: string;
    page?: string;
    children?: MenuChild[];
  };

  // Unify header for logged-in students/guests: show DashboardHeader on public pages too.
  // Admin/staff/dev/instructor keep the marketing header with the switch-to-dashboard button.
  const elevatedRoles = ['dev', 'admin', 'staff', 'instructor'];
  const hasElevatedRole = userRoles.some(r => elevatedRoles.includes(r));
  if (user && !hasElevatedRole) {
    return <DashboardHeader />;
  }

  const lang = (i18n.language || "en").startsWith("es") ? "es" : "en";

  const fallbackMenu: MenuItem[] = [
    { label: t('nav.courses'), href: "/videos", page: "/videos" },
    { label: t('nav.pricing'), href: "#pricing", page: "/#pricing" },
    {
      label: t('nav.more', 'Explore'),
      page: "/more",
      children: [
        { label: t('nav.creators', 'Creators'), href: "/creators", page: "/creators" },
        { label: t('nav.kids', 'Kids'), href: "/kids", page: "/kids" },
        { label: t('nav.training', 'Training'), href: "/training", page: "/training" },
        { label: t('nav.news', 'News'), href: "/news", page: "/news" },
        { label: t('nav.press', 'Press'), href: "/press", page: "/press" },
      ],
    },
    { label: t('nav.about_us'), href: "/aboutus", page: "/aboutus" },
  ];

  const menuItems: MenuItem[] = (headerTree && headerTree.length)
    ? headerTree.map((node) => {
        const label = lang === "es" ? node.label_es : node.label_en;
        const isHash = node.link_type === "route" && node.link_value?.startsWith("/#");
        const href = node.link_type === "group"
          ? undefined
          : (isHash ? node.link_value!.slice(1) : node.link_value || undefined);
        const page = node.link_type === "group" ? "/more" : (node.link_value || undefined);
        return {
          label,
          href: href ?? undefined,
          page,
          children: node.children.length
            ? node.children.map((c) => ({
                label: lang === "es" ? c.label_es : c.label_en,
                href: c.link_value?.startsWith("/#") ? c.link_value.slice(1) : (c.link_value || "#"),
                page: c.link_value || "/",
              }))
            : undefined,
        };
      })
    : fallbackMenu;

  const scrollToHash = (hash: string) => {
    const id = hash.replace(/^#/, '');
    const container = document.getElementById('page-scroll');
    const el = document.getElementById(id);
    if (el) {
      if (container && container.contains(el)) {
        container.scrollTo({ top: el.offsetTop, behavior: 'smooth' });
      } else {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const handleAllAccessClick = () => {
    if (isIndexPage) {
      scrollToHash('#pricing');
    } else {
      navigate('/#pricing');
    }
    setIsMenuOpen(false);
  };

  // Instant Y-scroll reset — runs BEFORE route transition so the slide
  // animation never plays diagonally (Y first, then X).
  const resetScrollInstant = () => {
    const container = document.getElementById('page-scroll');
    if (container) container.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  const smoothScrollToTop = () => {
    const container = document.getElementById('page-scroll');
    if (container) container.scrollTo({ top: 0, behavior: 'smooth' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleMenuClick = (item: MenuItem | MenuChild, e: React.MouseEvent) => {
    e.preventDefault();

    if (!item.href || !item.page) return;

    // Hash links (anchors on the homepage)
    if (item.href.startsWith('#')) {
      if (isIndexPage) {
        scrollToHash(item.href);
      } else {
        // Snap Y to top first so the slide animation is purely horizontal,
        // then navigate; Index handles scrolling to the hash target on mount.
        resetScrollInstant();
        requestAnimationFrame(() => navigate(item.page));
      }
      setIsMenuOpen(false);
      return;
    }

    // Regular routes — if already on it, smooth-scroll to top.
    if (location.pathname === item.page) {
      smoothScrollToTop();
    } else {
      resetScrollInstant();
      requestAnimationFrame(() => navigate(item.page));
    }
    setIsMenuOpen(false);
  };

  const handleLogoClick = () => {
    if (!user) {
      // Not signed in: logo always goes to the public home landing page
      if (isIndexPage) {
        smoothScrollToTop();
      } else {
        resetScrollInstant();
        navigate('/');
      }
      return;
    }

    if (isIndexPage) {
      smoothScrollToTop();
    } else {
      resetScrollInstant();
      requestAnimationFrame(() => navigate('/'));
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b shadow-soft transition-colors bg-background/95 backdrop-blur-sm border-border">
      <div className="container mx-auto px-4 lg:px-6">
        <div className="flex items-center justify-between h-[65px] lg:h-20">
          
          <button 
            type="button"
            onClick={handleLogoClick}
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
          >
            <img 
              src={siteSettings?.logo_url || "/iamerica-logo.svg"} 
              alt={t("nav.logo_alt")} 
              className="w-[3.08rem] h-[3.08rem] object-contain animate-fade-in"
            />
            <div className="flex flex-col text-center items-center animate-fade-in [animation-delay:0.2s] opacity-0 [animation-fill-mode:forwards] -ml-[5px]">
              <span className="block w-full text-[1.56rem] lg:text-[1.8rem] font-bold leading-tight text-[#212224] tracking-[0.03375em] text-center whitespace-nowrap max-w-[16rem] truncate">
                {siteSettings?.site_name || "iAmerica"}
              </span>
              <span className="block w-full text-[9px] lg:text-[10px] font-extrabold uppercase tracking-[0.06em] leading-tight -mt-0.5 text-[#324e8b] text-center whitespace-nowrap max-w-[16rem] truncate">
                {siteSettings?.slogan_frontend || t("nav.slogan_fallback")}
              </span>
            </div>
          </button>


          
          <div className="hidden lg:flex items-center gap-4 xl:gap-8">
            {menuItems.map((item) => {
              const isLibrary = item.page === "/videos";
              const hasDropdown = item.children || isLibrary;
              if (hasDropdown) {
                const isOpen = isLibrary ? isLibraryOpen : isMoreOpen;
                const setOpen = isLibrary ? setIsLibraryOpen : setIsMoreOpen;
                const timerRef = isLibrary ? libraryCloseTimer : moreCloseTimer;
                const openMenu = () => {
                  if (timerRef.current) {
                    window.clearTimeout(timerRef.current);
                    timerRef.current = null;
                  }
                  setOpen(true);
                };
                const scheduleClose = () => {
                  if (timerRef.current) window.clearTimeout(timerRef.current);
                  timerRef.current = window.setTimeout(() => setOpen(false), 180);
                };
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={openMenu}
                    onMouseLeave={scheduleClose}
                  >
                    <a
                      href={item.href || '#'}
                      onClick={(e) => {
                        if (isLibrary) {
                          e.preventDefault();
                          setIsLibraryOpen(false);
                          resetScrollInstant();
                          requestAnimationFrame(() => navigate('/videos'));
                        } else {
                          e.preventDefault();
                        }
                      }}
                      className="transition-colors font-medium flex items-center gap-1 text-foreground hover:text-primary cursor-pointer"
                      aria-haspopup="true"
                      aria-expanded={isOpen}
                    >
                      {item.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </a>
                      {isOpen && (
                        <div
                          className={`absolute top-full pt-3 z-50 ${isLibrary ? 'left-0' : 'left-1/2 -translate-x-1/2'}`}
                          onMouseEnter={openMenu}
                          onMouseLeave={scheduleClose}
                        >
                        {isLibrary ? (
                          <VideoLibraryMegaMenu
                            onNavigate={(path) => {
                              setIsLibraryOpen(false);
                              resetScrollInstant();
                              requestAnimationFrame(() => navigate(path));
                            }}
                          />
                        ) : (
                          <div className="bg-background border border-border rounded-lg shadow-strong p-2 min-w-[160px]">
                            {item.children?.map((child) => (
                              <a
                                key={child.label}
                                href={isIndexPage ? child.href : child.page}
                                onClick={(e) => {
                                  handleMenuClick(child, e);
                                  setIsMoreOpen(false);
                                }}
                                className="block px-3 py-2 rounded-md text-sm font-medium text-foreground hover:bg-muted hover:text-primary"
                              >
                                {child.label}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <a
                  key={item.label}
                  href={isIndexPage ? item.href : item.page}
                  onClick={(e) => handleMenuClick(item, e)}
                  className="transition-colors font-medium flex items-center gap-2 text-foreground hover:text-primary"
                >
                  {item.label}
                </a>
              );
            })}
          </div>

          
          <div className="hidden lg:flex items-center min-w-0">
            <HeaderSearch variant="desktop" />
          </div>

          
          <div className="hidden lg:flex items-center gap-[15px]">
            <LanguageSwitcher variant="dark" />
            {desktopActions}
            {user && userInstitution && (
              <Button
                onClick={handleSwitchInstitution}
                variant="outline"
                size="sm"
                className="gap-1.5 px-2 text-xs"
              >
                <RefreshCcw className="h-3.5 w-3.5" />
                {institutionToggleLabel}
              </Button>
            )}
            {user ? (
              (location.pathname.startsWith('/dashboard') || location.pathname.startsWith('/staff') || location.pathname.startsWith('/student') || location.pathname.startsWith('/guest')) ? (
                <Button
                  onClick={async () => { try { await signOut(); } finally { navigate('/', { replace: true }); } }}
                  className="gap-2 min-w-[140px] bg-logout text-white hover:bg-logout-hover"
                >
                  {t('common:log_out', 'Log out')}
                </Button>
              ) : (
                <Button
                  onClick={() => navigate(getDashboardRoute())}
                  className="gap-2 min-w-[140px] bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  <User className="h-4 w-4" />
                  {t('common:dashboard')}
                </Button>
              )
            ) : (
              <>
                <Button
                  onClick={() => navigate('/auth')}
                  variant="outline"
                  className="gap-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                >
                  {t('common:sign_in')}
                </Button>
                <Button
                  onClick={() => navigate('/auth?tab=signup')}
                  className="bg-gradient-primary text-primary-foreground shadow-medium hover:opacity-90 transition-opacity"
                >
                  {t('common:sign_up')}
                </Button>
              </>
            )}
          </div>

          
          <div className="lg:hidden flex items-center gap-1">
            <LanguageSwitcher variant="dark" />
            <button
              onClick={() => setIsMobileSearchOpen(true)}
              className="p-2 transition-colors text-foreground hover:text-primary"
              aria-label={t('common:search.aria', 'Search')}
            >
              <Search className="w-5 h-5" />
            </button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 transition-colors text-foreground hover:text-primary"
              aria-label={t('common:accessibility.toggle_menu')}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        
        {isMobileSearchOpen && (
          <div className="lg:hidden absolute inset-x-0 top-0 h-[65px] bg-background border-b border-border flex items-center gap-2 px-3 z-[55] animate-fade-in">
            <div className="flex-1">
              <HeaderSearch variant="mobile" onCloseMobile={() => setIsMobileSearchOpen(false)} />
            </div>
            <button
              onClick={() => setIsMobileSearchOpen(false)}
              className="p-2 text-foreground hover:text-primary"
              aria-label={t('common:close', 'Close')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Mobile Drawer (right edge) — portaled to body so #root push-translate doesn't move it */}
      {createPortal(
        <>
          <div
            className={`lg:hidden fixed inset-0 z-[60] transition-opacity duration-300 ${
              isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
            }`}
            onClick={() => setIsMenuOpen(false)}
            aria-hidden={!isMenuOpen}
          />
          <aside
            className={`lg:hidden fixed top-0 right-0 h-screen w-[85vw] max-w-[320px] z-[70] bg-background border-l border-border shadow-strong transform transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
              isMenuOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            role="dialog"
            aria-modal="true"
            aria-hidden={!isMenuOpen}
          >
            <div className="overflow-y-auto h-screen px-4 pt-5 pb-6">
              <div className="flex flex-col gap-4 pb-6">
                
                <div className="flex lg:hidden items-center justify-between gap-2 pb-4 border-b border-border text-primary">
                  <SocialIcons size={18} iconClassName="h-9 w-9 hover:bg-primary/10" />
                  <button
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleLogoClick();
                    }}
                    aria-label={t('common:accessibility.go_home')}
                    className="rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <img
                      src={siteSettings?.logo_url || "/iamerica-logo.svg"}
                      alt="iAmerica"
                      className="w-9 h-9 object-contain"
                    />
                  </button>
                </div>
                {menuItems.map((item) => {
                  const isLibrary = item.page === "/videos";
                  const hasChildren = !!item.children;
                  if (isLibrary) {
                    return (
                      <div key={item.label} className="flex flex-col">
                        <div className="flex items-center justify-between">
                          <a
                            href={item.page}
                            onClick={(e) => handleMenuClick(item, e)}
                            className="transition-colors font-medium py-2 flex-1 text-foreground hover:text-primary"
                          >
                            {item.label}
                          </a>
                          <button
                            onClick={() => setIsLibraryMobileOpen((v) => !v)}
                            className="p-2 text-muted-foreground hover:text-primary"
                            aria-label="Toggle video list"
                            aria-expanded={isLibraryMobileOpen}
                          >
                            <ChevronDown className={`w-4 h-4 transition-transform ${isLibraryMobileOpen ? "rotate-180" : ""}`} />
                          </button>
                        </div>
                        {isLibraryMobileOpen && (
                          <VideoLibraryMegaMenu
                            onNavigate={(path) => {
                              setIsLibraryMobileOpen(false);
                              setIsMenuOpen(false);
                              resetScrollInstant();
                              requestAnimationFrame(() => navigate(path));
                            }}
                          />
                        )}
                      </div>
                    );
                  }
                  if (hasChildren) {
                    return (
                      <div key={item.label} className="flex flex-col">
                        <div className="flex items-center justify-between">
                          <span className="font-medium py-2 flex-1 text-foreground">
                            {item.label}
                          </span>
                          <button
                            onClick={() => setIsMoreMobileOpen((v) => !v)}
                            className="p-2 text-muted-foreground hover:text-primary"
                            aria-label="Toggle more menu"
                            aria-expanded={isMoreMobileOpen}
                          >
                            <ChevronDown className={`w-4 h-4 transition-transform ${isMoreMobileOpen ? "rotate-180" : ""}`} />
                          </button>
                        </div>
                        {isMoreMobileOpen && (
                          <div className="pl-3 pb-2 border-l-2 border-border ml-1 flex flex-col gap-1">
                            {item.children?.map((child) => (
                              <a
                                key={child.label}
                                href={isIndexPage ? child.href : child.page}
                                onClick={(e) => handleMenuClick(child, e)}
                                className="transition-colors font-medium py-2 text-foreground hover:text-primary"
                              >
                                {child.label}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return (
                    <a
                      key={item.label}
                      href={isIndexPage ? item.href : item.page}
                      onClick={(e) => handleMenuClick(item, e)}
                      className="transition-colors font-medium py-2 flex items-center gap-2 text-foreground hover:text-primary"
                    >
                      {item.label}
                    </a>
                  );
                })}
                <div className="py-2">
                  <LanguageSwitcher variant="dark" />
                </div>
                 <div className="flex flex-col gap-3 pt-4 border-t border-border">
                   {user && userInstitution && (
                     <Button
                       onClick={() => {
                         setIsMenuOpen(false);
                         handleSwitchInstitution();
                       }}
                       variant="outline"
                       className="w-full gap-2 justify-start"
                     >
                       <RefreshCcw className="h-4 w-4" />
                       {institutionToggleLabel}
                     </Button>
                   )}
                   {user ? (
                     <div className="flex gap-2">
                      {(location.pathname.startsWith('/dashboard') || location.pathname.startsWith('/staff') || location.pathname.startsWith('/student') || location.pathname.startsWith('/guest')) ? (
                        <Button
                          onClick={async () => {
                            setIsMenuOpen(false);
                            try { await signOut(); } finally { navigate('/', { replace: true }); }
                          }}
                          className="flex-1 gap-2 bg-logout text-white hover:bg-logout-hover"
                        >
                          {t('common:log_out', 'Log out')}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => {
                            setIsMenuOpen(false);
                            resetScrollInstant();
                            requestAnimationFrame(() => navigate(getDashboardRoute()));
                          }}
                          className="flex-1 gap-2 bg-primary hover:bg-primary/90 text-primary-foreground"
                        >
                          <User className="h-4 w-4" />
                          {t('common:dashboard')}
                        </Button>
                      )}
                      <div className="flex-1">
                        <CartDrawer triggerVariant="button" />
                      </div>
                    </div>
                  ) : (
                    <>
                      <Button
                        onClick={() => {
                          setIsMenuOpen(false);
                          resetScrollInstant();
                          requestAnimationFrame(() => navigate('/auth'));
                        }}
                        variant="outline"
                        className="w-full gap-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                      >
                        {t('common:sign_in')}
                      </Button>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            setIsMenuOpen(false);
                            resetScrollInstant();
                            requestAnimationFrame(() => navigate('/auth?tab=signup'));
                          }}
                          className="flex-1 bg-gradient-primary text-primary-foreground shadow-medium hover:opacity-90 transition-opacity"
                        >
                          {t('common:sign_up')}
                        </Button>
                        <div className="flex-1">
                          <CartDrawer triggerVariant="button" />
                        </div>
                      </div>
                    </>
                  )}
                </div>

                
                <Button
                  type="button"
                  variant="default"
                  onClick={() => setIsMenuOpen(false)}
                  className="self-start mt-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                  aria-label="Close menu"
                >
                  <ChevronsLeft className="w-4 h-4" />
                  {t("common:back", "Back")}
                </Button>
              </div>
            </div>
          </aside>
        </>,
        document.body
      )}
      <InstitutionPausedNotice />
    </nav>
  );

};

export default Navigation;